import React, { useState, useEffect } from "react";
import "./pages.css";
import { getAllDataWithFlag } from "../services/CommonService";
import { imgURL } from "../components/Config";
import moment from "moment";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [lookupData, setLookupData] = useState([]);
  const [mastersData, setMastersData] = useState([]);
  const [typologiesData, setTypologiesData] = useState([]);
  const [themesData, setThemesData] = useState([]);
  const [isGridView, setIsGridView] = useState(true);

  // State for search inputs
  const [searchInputs, setSearchInputs] = useState({
    year: "",
    type: "",
    typology: "",
    district: "",
    state: "",
  });

  // Available filter options (will be populated dynamically)
  const [filterOptions, setFilterOptions] = useState({
    years: [],
    types: [],
    typologies: [],
    districts: [],
    states: [],
  });

  useEffect(() => {
    fetchProjects();
    const lookup = JSON.parse(localStorage.getItem('lookup'));
    if (lookup) {
      const sortedLookup = lookup.map(category => ({
        ...category,
        // Sort within each category if needed
      })).sort((a, b) => a.LookupValue?.localeCompare(b.LookupValue));
      setLookupData(sortedLookup);
    }
  }, []);

  // Apply filters whenever search inputs change
  useEffect(() => {
    applyFilters();
  }, [projects]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await getAllDataWithFlag(5);
      const projectData = response?.data?.project || [];
      const typologyData = JSON.parse(localStorage.getItem('typologyData'));
      const themeData = JSON.parse(localStorage.getItem('themeData'));
      const masterData = JSON.parse(localStorage.getItem('StateMaster'));
      setTypologiesData(typologyData);
      setThemesData(themeData);
      setMastersData(masterData);
      const stateMap = Object.fromEntries((masterData?.state || []).map(st => [st.id_mststate, st.statename]));
      const districtMap = Object.fromEntries((masterData?.district || []).map(dt => [dt.id_mstdistrict, dt.districtname]));
      const typologyMap = Object.fromEntries((typologyData || []).map(tp => [tp.id, tp.typology]));
      const themeMap = Object.fromEntries((themeData || []).map(th => [th.id, th.theme]));

      projectData.forEach((m) => {
        const stateIds = m.state?.split(',') || [];
        const districtIds = m.district?.split(',') || [];
        const typologyIds = m.typology?.split(',') || [];
        const themeIds = m.theme?.split(',') || [];

        m.statenames = stateIds.map(id => stateMap[id.trim()]).filter(Boolean);
        m.districtnames = districtIds.map(id => districtMap[id.trim()]).filter(Boolean);
        m.typologynames = typologyIds.map(id => typologyMap[id.trim()]).filter(Boolean);
        m.themenames = themeIds.map(id => themeMap[id.trim()]).filter(Boolean);
      });

      setProjects(projectData);
      setFilteredProjects(projectData);

      // Extract unique filter options from data
      if (projectData.length > 0) {
        extractFilterOptions(projectData);
      }

      setError(null);
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || err?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  // Extract unique values for each filter category
  const extractFilterOptions = (data) => {
    const currentYear = new Date().getFullYear();
    const allYears = Array.from(
      { length: currentYear - 2010 + 1 },
      (_, i) => 2010 + i
    );
    const options = {
      years: allYears,
      types: [...new Set(data.map((item) => item.type).filter(Boolean))],
      typologies: [
        ...new Set(data.map((item) => item.typology).filter(Boolean)),
      ],
      districts: [
        ...new Set(data.map((item) => item.district).filter(Boolean)),
      ],
      states: [...new Set(data.map((item) => item.state).filter(Boolean))],
    };

    setFilterOptions(options);
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Apply all active filters
  const applyFilters = () => {
    if (!projects.length) return;

    let filtered = [...projects];

    // Filter by each non-empty search input
    if (searchInputs.year) {
      filtered = filtered.filter((project) =>
        project.year?.toLowerCase().includes(searchInputs.year.toLowerCase())
      );
    }

    if (searchInputs.type) {
      filtered = filtered.filter((project) =>
        project.theme?.split(',')?.includes(searchInputs.type)
      );
    }

    if (searchInputs.typology) {
      filtered = filtered.filter((project) =>
        project.typology?.split(',')
          .includes(searchInputs.typology)
      );
    }

    if (searchInputs.district) {
      filtered = filtered.filter((project) =>
        project.district
          ?.toLowerCase()
          .includes(searchInputs.district.toLowerCase())
      );
    }

    if (searchInputs.state) {
      filtered = filtered.filter((project) =>
        project.state?.toLowerCase().includes(searchInputs.state.toLowerCase())
      );
    }

    setFilteredProjects(filtered);
  };

  // Search button handler
  const handleSearch = () => {
    applyFilters();
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchInputs({
      year: "",
      type: "",
      typology: "",
      district: "",
      state: "",
    });
    setFilteredProjects(projects);
  };

  // Sorting functionality
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedProjects = React.useMemo(() => {
    let sortableProjects = [...filteredProjects];
    if (sortConfig.key !== null) {
      sortableProjects.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableProjects;
  }, [projects, sortConfig, filteredProjects]);

  const getSortIcon = (columnName) => {
    if (sortConfig.key === columnName) {
      return sortConfig.direction === 'ascending' ? '↑' : '↓';
    }
    return '↕';
  };

  const changePage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Pagination
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(filteredProjects?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = sortedProjects?.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const banners = JSON.parse(localStorage.getItem('banners'));
  const bannerItem = (banners || []).find((r) => r.banner_type == 5);

  const dateFormat = (d) => d ? moment(d).format('DD-MM-YYYY') : 'N/A';

  return (
    <>
      <div className="project-search-container">
        <div className="banner-container">
          <img
            src={bannerItem?.image_path ? `${imgURL}${bannerItem?.image_path}` : '/assets/images/bannerDefault.jpg'}
            alt="Groundwater"
            className="img-fluid w-100"
            style={{ minHeight: '350px', maxHeight: '350px', objectFit: 'cover' }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
        </div>

        <div className="container text-center">
          <h2 className="title">Our Projects</h2>
          <div className="row">
            <div className="col-md-2">
              <select name="type" className="form-select w-full whitespace-normal break-words pe-5" value={searchInputs.type} onChange={handleInputChange}>
                <option value="">Select Theme</option>
                {(themesData?.length > 0) && themesData.map((type, idx) => (
                  <option key={idx} value={type.id}>{type.theme}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select name="typology" className="form-select w-full whitespace-normal break-words pe-5" value={searchInputs.typology} onChange={handleInputChange}>
                <option value="">Select Typology</option>
                {(typologiesData?.length > 0) && typologiesData.map((typology, idx) => (
                  <option key={idx} value={typology.id}>{typology.typology}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select name="state" className="form-select w-full whitespace-normal break-words pe-5" value={searchInputs.state} onChange={handleInputChange}>
                <option value="">Select State</option>
                {(mastersData?.state?.length > 0) && mastersData?.state.map((state, idx) => (
                  <option key={idx} value={state.id_mststate}>{state.statename}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select name="district" className="form-select w-full whitespace-normal break-words pe-5" value={searchInputs.district} onChange={handleInputChange}>
                <option value="">Select District</option>
                {(mastersData?.state?.length > 0) && mastersData.district.filter(m => m.id_mststate == searchInputs.state)?.map((district, idx) => (
                  <option key={idx} value={district.id_mstdistrict}>{district.districtname}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-md btn-warning rounded-pill me-3" onClick={handleSearch}>Search</button>
              <button className="clear-btn btn rounded-pill btn-secondary" onClick={clearFilters}>Clear</button>
            </div>
          </div>
        </div>

        <div className="container">
          {loading ? (
            <div className="loading-message">Loading projects...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            <div className="table-responsive table-bordered mt-4">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                      Title {getSortIcon('title')}
                    </th>
                    <th onClick={() => handleSort('typology')} style={{ cursor: 'pointer' }}>
                      Type {getSortIcon('typology')}
                    </th>
                    <th onClick={() => handleSort('theme')} style={{ cursor: 'pointer' }}>Theme {getSortIcon('theme')}</th>
                    <th onClick={() => handleSort('state')} style={{ cursor: 'pointer' }}>
                      State {getSortIcon('state')}
                    </th>
                    <th onClick={() => handleSort('district')} style={{ cursor: 'pointer' }}>District {getSortIcon('districtnames')}</th>
                    <th width="120" onClick={() => handleSort('start_date')} style={{ cursor: 'pointer' }}>
                      Duration {getSortIcon('start_date')}
                    </th>
                    <th width="100" onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                      Status {getSortIcon('status')}
                    </th>
                    <th>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((project) => (
                    <tr key={project.id}>
                      <td>{project.title}</td>
                      <td>{Array.isArray(project.typologynames) ? project.typologynames?.join(', ') : project.typologynames}</td>
                      <td>{Array.isArray(project.themenames) ? project.themenames?.join(', ') : project.themenames}</td>
                      <td>{Array.isArray(project.statenames) ? project.statenames?.join(', ') : project.statenames}</td>
                      <td>{Array.isArray(project.districtnames) ? project.districtnames?.join(', ') : project.districtnames}</td>
                      <td>
                        {dateFormat(project.start_date)} to {dateFormat(project.end_date)}
                      </td>
                      <td>
                        <span
                          className={`badge ${project.status === 2
                            ? "bg-success"
                            : project.status === 1
                              ? "bg-primary"
                              : "bg-warning"
                            }`}
                        >
                          {(lookupData?.length > 0) && (project.status ? lookupData?.find(m => m.LookupCode == project.status && m.Flag === "13")?.LookupValue : '')}
                        </span>
                      </td>
                      <td className='text-center'>
                        <Link to={`/project-details/${project.id}`}>
                          <i className='bi bi-eye text-primary'
                            style={{ cursor: 'pointer' }}></i>
                        </Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          <button
            onClick={() => changePage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            &lt;
          </button>

          {[...Array(totalPages)].map((_, index) => {
            const page = index + 1;
            return (
              <button
                key={page}
                onClick={() => changePage(page)}
                style={{
                  fontWeight: currentPage === page ? "bold" : "normal",
                  color: currentPage === page ? "#2e1e82" : "black",
                  border: "1px solid gray",
                  padding: "5px 10px",
                  cursor: "pointer",
                }}
              >
                {page}
              </button>
            );
          })}

          <button
            onClick={() => changePage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            &gt;
          </button>
        </div>
      </div>
    </>
  );
}

export default ProjectsPage;
